import ArrowUpIcon from "@/assets/icons/arrow-top.svg";
import waitingVistaAssistantAnimation from "@/assets/lotties/logo_cercle_opaque.json";
import listeningVistaAssistantAnimation from "@/assets/lotties/logo_traite.json";
import MicrophoneButton from "@/components/customViews/MicrophoneButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useMicrophone from "@/hooks/adapters/useMicrophone";
import { cn } from "@/lib/utils";
import {
    openAssistant,
    openAssistantWithPrefill,
} from "@/stores/assistantStore";
import Lottie from "lottie-react";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

const MAX_INPUT_LENGTH = 500;

interface AskVistaInputProps {
    className?: string;
}

const AskVistaInput = ({ className }: AskVistaInputProps) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const [isListening, setIsListening] = useState(false);

    const {
        handleStartListening,
        handleStopListening,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        isDictationDisabled,
        listening,
        resetTranscript,
    } = useMicrophone(setInputValue, MAX_INPUT_LENGTH);

    const handleSubmitInput = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const query = inputValue.trim();
        setInputValue("");
        openAssistant(query || undefined);
    };

    return (
        <form
            className={cn(
                "flex min-w-0 max-w-full items-center gap-1 overflow-hidden rounded-full bg-white p-1.5 px-2 text-foreground sm:gap-2",
                className,
            )}
            onSubmit={(e) => {
                handleSubmitInput(e);
            }}
        >
            <Button
                className="flex-shrink-0"
                onClick={() => {
                    openAssistantWithPrefill(inputValue.trim() || undefined);
                    setInputValue("");
                }}
                size="blank"
                type="button"
                variant="invisible"
            >
                <Lottie
                    animationData={
                        isListening
                            ? listeningVistaAssistantAnimation
                            : waitingVistaAssistantAnimation
                    }
                    className="h-8 w-8 sm:h-10 sm:w-10"
                    loop
                />
            </Button>
            <Input
                className="min-w-0 flex-1 text-sm"
                onBlur={() => {
                    setIsListening(false);
                }}
                onChange={(e) => {
                    if (e.target.value.length > MAX_INPUT_LENGTH) return;
                    setInputValue(e.target.value);
                    resetTranscript();
                }}
                onFocus={() => {
                    setIsListening(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        const formEvent = new Event(
                            "submit",
                        ) as unknown as FormEvent<HTMLFormElement>;
                        handleSubmitInput(formEvent);
                    }
                }}
                placeholder={t("vistassistant.headerInputPlaceholder")}
                value={inputValue}
                variant="ghost"
            />
            <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
                <MicrophoneButton
                    browserSupportsSpeechRecognition={
                        browserSupportsSpeechRecognition
                    }
                    className="h-7 w-7 sm:h-8 sm:w-8"
                    isDictationDisabled={isDictationDisabled}
                    isMicrophoneAvailable={isMicrophoneAvailable}
                    listening={listening}
                    startListening={() => {
                        handleStartListening();
                        setIsListening(true);
                    }}
                    stopListening={() => {
                        handleStopListening();
                        setIsListening(false);
                    }}
                />
                <Button
                    className="h-7 w-7 rounded-full p-0 sm:h-8 sm:w-8"
                    disabled={!inputValue.trim()}
                    type="submit"
                    variant="default"
                >
                    <img alt="" className="mt-[-3px]" src={ArrowUpIcon} />
                </Button>
            </div>
        </form>
    );
};

export default AskVistaInput;
